import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import dynamicfunction from '../../../utils/dynamicfunction';
import { convertIntoArray, getContentKeyAccToLang } from '../../../utils/utils';
import { Chips, CustomDatePicker, ErrorText, RadioButton, Text } from '../../CorporateForm/components';
import { RowView, Dropdown } from '../../../components';
import CustomInput from '../../../components/CustomInput';
import Button from '../../../components/Button';
import { logoutUser } from '../../../store/actions/authActions';
import { useNavigate } from 'react-router';
import { individual_personal_information_validation_schema } from '../../../validations_schemas/individual_personal_information_schema';
import {
  getIndPersonalInformationDropdownData,
  postIndPersonalInformation,
  saveIndKycPersonalInformation,
} from '../../../store/actions/individualAction';
import HijriDatePicker from '../../../components/HijriDatePicker';
import dayjs from 'dayjs';
import CustomModal from '../../../components/CustomModal';
import { fontSizes } from '../../../constants/constants';
import { setRejectionMsg } from '../../../store/slices/constantSlice';
import KeepValuesOnLanguageChange from '../../../components/KeepValuesOnLanguageChange';

const PersonalInformation = ({ onDone }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [applicationRejectedModal, setApplicationRejectedModal] = useState(false);

  const handleRejectionModalClose = () => {
    setApplicationRejectedModal(false);
    dispatch(setRejectionMsg(null));
  };

  const { isArabic, TextWeight } = dynamicfunction();

  const {
    individualContent,
    language
  } = useSelector((state) => state.content);

  const {
    ind_post_loading,
    ind_personal_information_data,
    ind_personal_information_dropdown_data
  } = useSelector((state) => state.individual);

  const {
    client_application_id,
    is_kyc,
    applicant_type,
    rejection_msg
  } = useSelector((state) => state.constant);

  // Unsaved form values captured on language switch; in memory only, so a hard reload clears them.
  const draftValues = useRef(null);

  useEffect(() => {
    if (typeof rejection_msg === 'string') {
      const comment = rejection_msg?.trim();
      if (comment) {
        setApplicationRejectedModal(true);
      }
    }
  }, []);

  useEffect(() => {
    getData();
  }, [language]);

  const getData = async () => {
    await dispatch(getIndPersonalInformationDropdownData(client_application_id));
  }

  const is_editable = applicant_type === 'KSA National' || applicant_type === 'KSA Resident' ? false : true;
  const is_ksa = applicant_type === 'KSA National' || applicant_type === 'KSA Resident';

  const getContent = (key) => {
    return getContentKeyAccToLang(
      individualContent?.personal_information?.data?.attributes,
      key,
      isArabic
    );
  };

  const myArray = convertIntoArray(ind_personal_information_dropdown_data);

  const findNationality = (id) => myArray?.countryListWithCode?.find((item) => String(item.id) === String(id));

  const onSubmit = (values) => {
    try {
      const api_data = {
        ...ind_personal_information_data,
        titleId: values?.titleId?.id?.toString() ?? [],
        otherTitle: values?.otherTitle ?? '',
        firstNameEnglish: values?.firstNameEnglish ?? '',
        secondNameEnglish: values?.secondNameEnglish ?? '',
        thirdNameEnglish: values?.thirdNameEnglish ?? '',
        lastNameEnglish: values?.lastNameEnglish ?? '',
        genderId: Number(values?.genderId?.id) ?? [],
        dateOfBirth: values?.dateOfBirth ?? null,
        placeOfBirth: values?.placeOfBirth ?? '',
        nationalityId: Number(values?.nationalityId?.id) ?? null,
        areYouAlsoANationalOfAnotherCountry: values?.areYouAlsoANationalOfAnotherCountry?.toString() === '1' ? true : false,
        anotherNationalityId: values.anotherNationalityId?.map((item) => Number(item.id)) ?? values.anotherNationalityId?.id,
        anotherNationalityIdString: values.anotherNationalityId?.map((item) => item.countryCode) ?? [],
        identificationNumber: values?.identificationNumber,
        noOfDependents: values?.noOfDependents?.toString(),
        identificationNumberExpiryDate: values?.identificationNumberExpiryDate,
        identificationNumberExpiryDateGRG: values?.identificationNumberExpiryDateGRG,
        maritalStatusId: Number(values?.maritalStatusId?.id) ?? null,
        levelOfEducationId: values?.levelOfEducationId ?? null,
        residenceTypeId: values?.residenceTypeId ?? null,
        approxNetWorthId: Number(values?.approxNetWorthId?.id),
        approxAnnualIncomeId: Number(values?.approxAnnualIncomeId?.id),
      };

      is_kyc
        ? dispatch(
          saveIndKycPersonalInformation(api_data, () => {
            values?.isSaveExist ? navigate('/funds/available-funds') : onDone();
          }),
        )
        : dispatch(
          postIndPersonalInformation(api_data, () => {
            values?.isSaveExist ? dispatch(logoutUser()) : onDone();
          }),
        );
    } catch (error) {
    }
  };

  const optionsYesOrNO = [
    { id: 1, name: getContent('yes') },
    { id: 0, name: getContent('no') },
  ];

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={draftValues.current ? {
          ...draftValues.current,
          // Re-pick dropdown objects from the refetched list so labels show in the new language.
          titleId: myArray?.title?.find((item) => item.id === String(draftValues.current?.titleId?.id)) || null,
          genderId: myArray?.gender?.find((item) => item.id === String(draftValues.current?.genderId?.id)) || null,
          nationalityId: findNationality(draftValues.current?.nationalityId?.id)
            ? {
              id: String(draftValues.current?.nationalityId?.id),
              name: findNationality(draftValues.current?.nationalityId?.id)?.name?.displayName,
            }
            : '',
          anotherNationalityId:
            draftValues.current?.anotherNationalityId
              ?.map((val) => {
                const country = myArray?.countryListAnotherCountryWithCode?.find((item) => Number(item?.id) === Number(val?.id));
                return country
                  ? {
                    id: country?.id,
                    name: country?.name?.displayName,
                    countryCode: country?.name?.countryCode,
                  }
                  : null;
              })
              .filter(Boolean) ?? [],
          maritalStatusId: myArray?.maritalStatus?.find((item) => item.id === String(draftValues.current?.maritalStatusId?.id)) || null,
          approxNetWorthId: myArray?.approxNetWorth?.find((item) => item.id === String(draftValues.current?.approxNetWorthId?.id)) || null,
          approxAnnualIncomeId:
            myArray?.approxAnnualIncome?.find((item) => item.id === String(draftValues.current?.approxAnnualIncomeId?.id)) || null,
        } : {
          titleId:
            myArray?.title?.find((item) => item.id === String(ind_personal_information_data?.titleId)) || null,
          otherTitle: ind_personal_information_data?.otherTitle || '',
          firstNameEnglish: ind_personal_information_data?.firstNameEnglish || '',
          secondNameEnglish: ind_personal_information_data?.secondNameEnglish || '',
          thirdNameEnglish: ind_personal_information_data?.thirdNameEnglish || '',
          lastNameEnglish: ind_personal_information_data?.lastNameEnglish || '',
          genderId:
            myArray?.gender?.find((item) => item.id === String(ind_personal_information_data?.genderId)) || null,
          dateOfBirth: ind_personal_information_data?.dateOfBirth
            ? ind_personal_information_data?.dateOfBirth?.split("T")[0]
            : dayjs().subtract(18, "years").format("YYYY-MM-DD"),
          placeOfBirth: ind_personal_information_data?.placeOfBirth || '',
          nationalityId:
            myArray?.countryListWithCode?.find((item) => String(item.id) === String(ind_personal_information_data?.nationalityId))
              ? {
                id: String(ind_personal_information_data?.nationalityId),
                name: myArray.countryListWithCode.find((item) => String(item.id) === String(ind_personal_information_data?.nationalityId))?.name
                  ?.displayName,
              }
              : '',
          areYouAlsoANationalOfAnotherCountry:
            ind_personal_information_data?.areYouAlsoANationalOfAnotherCountry === true
              ? 1
              : 0,
          anotherNationalityId:
            ind_personal_information_data?.anotherNationalityId
              ?.map((id) => {
                const country = myArray?.countryListAnotherCountryWithCode?.find((item) => Number(item?.id) === Number(id));
                return country
                  ? {
                    id: country?.id,
                    name: country?.name?.displayName,
                    countryCode: country?.name?.countryCode,
                  }
                  : null;
              })
              .filter(Boolean) ?? [],
          identificationNumber: ind_personal_information_data?.identificationNumber || '',
          noOfDependents: ind_personal_information_data?.noOfDependents || '',
          identificationNumberExpiryDate: ind_personal_information_data?.identificationNumberExpiryDate,
          identificationNumberExpiryDateGRG: ind_personal_information_data?.identificationNumberExpiryDateGRG || '',
          maritalStatusId:
            myArray?.maritalStatus?.find((item) => item.id === String(ind_personal_information_data?.maritalStatusId)) || null,
          levelOfEducationId: ind_personal_information_data?.levelOfEducationId?.toString() ?? '',
          residenceTypeId: ind_personal_information_data?.residenceTypeId?.toString() ?? '',
          approxNetWorthId:
            myArray?.approxNetWorth?.find((item) => item.id === String(ind_personal_information_data?.approxNetWorthId)) || null,
          approxAnnualIncomeId:
            myArray?.approxAnnualIncome?.find((item) => item.id === String(ind_personal_information_data?.approxAnnualIncomeId)) || null,
          isSaveExist: false,
        }}
        validationSchema={individual_personal_information_validation_schema}
        onSubmit={(values) => {
          onSubmit(values);
        }}
        validateOnChange={true}
        validateOnBlur={true}
      >
        {({ handleSubmit, setFieldValue, values, touched, errors, setFieldTouched }) => {
          return (
            <div>
              <KeepValuesOnLanguageChange onLanguageChange={(currentValues) => (draftValues.current = currentValues)} />
              <RowView className='gap-7' isResponsive>
                <Dropdown
                  label={getContent('title')}
                  placeholder={getContent('please_select')}
                  options={myArray?.title}
                  selectedValue={values?.titleId}
                  valueKey={'name'}
                  isRequired
                  isSearch
                  onSelect={(val) => {
                    setFieldValue('titleId', val);
                    setFieldTouched('titleId', false);
                  }}
                  errorMessage={getContent(touched?.titleId ? errors?.titleId : '')}
                />
                <div className='lg:w-full hidden lg:block'></div>
                <div className='lg:w-full hidden lg:block'></div>
              </RowView>
              {values?.titleId?.id === 24 && (
                <RowView className='pt-3 sm:pt-5'>
                  <CustomInput
                    label={getContent('others_please_spacify')}
                    value={values?.otherTitle}
                    isRounded
                    isRequired={true}
                    onChange={(e) => {
                      setFieldValue('otherTitle', e.target.value);
                      setFieldTouched('otherTitle', true, false);
                    }}
                    placeholder={getContent('please_select')}
                    errorMessage={getContent(touched?.otherTitle ? errors?.otherTitle : '')}
                    maxLength={50}
                  />
                </RowView>
              )}

              <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                <Text text={getContent('customer_full_name')} isTruncate={false} weight={TextWeight.regular} className={'block'} />
              </RowView>

              <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                <CustomInput
                  label={getContent('first_name')}
                  value={values?.firstNameEnglish}
                  isRounded
                  isRequired={true}
                  onChange={(e) => {
                    setFieldValue('firstNameEnglish', e.target.value);
                    setFieldTouched('firstNameEnglish', true, false);
                  }}
                  maxLength={100}
                  placeholder={getContent('first_name')}
                  errorMessage={getContent(touched?.firstNameEnglish ? errors?.firstNameEnglish : '')}
                />
                <CustomInput
                  label={getContent('second_name')}
                  value={values?.secondNameEnglish}
                  isRounded
                  isRequired={true}
                  onChange={(e) => {
                    setFieldValue('secondNameEnglish', e.target.value);
                    setFieldTouched('secondNameEnglish', true, false);
                  }}
                  placeholder={getContent('second_name')}
                  errorMessage={getContent(touched?.secondNameEnglish ? errors?.secondNameEnglish : '')}
                />
                <CustomInput
                  label={getContent('third_name')}
                  value={values?.thirdNameEnglish}
                  isRounded
                  isRequired={true}
                  onChange={(e) => {
                    setFieldValue('thirdNameEnglish', e.target.value);
                    setFieldTouched('thirdNameEnglish', true, false);
                  }}
                  placeholder={getContent('third_name')}
                  errorMessage={getContent(touched?.thirdNameEnglish ? errors?.thirdNameEnglish : '')}
                />
              </RowView>

              <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                <CustomInput
                  label={getContent('last_name')}
                  value={values?.lastNameEnglish}
                  isRounded
                  isRequired={true}
                  onChange={(e) => {
                    setFieldValue('lastNameEnglish', e.target.value);
                    setFieldTouched('lastNameEnglish', true, false);
                  }}
                  placeholder={getContent('last_name')}
                  errorMessage={getContent(touched?.lastNameEnglish ? errors?.lastNameEnglish : '')}
                />
                <Dropdown
                  label={getContent('select_gender')}
                  placeholder={getContent('select_gender')}
                  options={myArray?.gender}
                  selectedValue={values?.genderId}
                  valueKey={'name'}
                  isRequired
                  isSearch
                  onSelect={(val) => {
                    setFieldValue('genderId', val);
                    setFieldTouched('genderId', false);
                  }}
                  errorMessage={getContent(touched?.genderId ? errors?.genderId : '')}
                />
                <CustomDatePicker
                  label={getContent('date_of_birth')}
                  selectedDate={values?.dateOfBirth ? new Date(values?.dateOfBirth) : null}
                  placeholder='YYYY/MM/DD'
                  onChange={(val) => {
                    setFieldValue('dateOfBirth', val ? val.toISOString().split('T')[0] : null);
                    setFieldTouched('dateOfBirth', false);
                  }}
                  maxDate={new Date(dayjs().subtract(18, 'years'))}
                  isRequired={true}
                  errorMessage={getContent(touched?.dateOfBirth ? errors?.dateOfBirth : '')}
                />
              </RowView>

              <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                <CustomInput
                  label={getContent('place_of_birth')}
                  value={values?.placeOfBirth}
                  isRounded
                  isRequired={true}
                  onChange={(e) => {
                    setFieldValue('placeOfBirth', e.target.value);
                    setFieldTouched('placeOfBirth', true, false);
                  }}
                  placeholder={getContent('place_of_birth')}
                  errorMessage={getContent(touched?.placeOfBirth ? errors?.placeOfBirth : '')}
                />
                <Dropdown
                  label={getContent('citizenship')}
                  placeholder={getContent('citizenship')}
                  options={myArray?.countryListWithCode?.map((item) => ({
                    id: item?.id,
                    name: item?.name?.displayName,
                  }))}
                  selectedValue={values?.nationalityId}
                  valueKey={'name'}
                  isRequired
                  isSearch
                  onSelect={(val) => {
                    setFieldValue('nationalityId', val);
                    setFieldTouched('nationalityId', false);
                  }}
                  errorMessage={getContent(touched?.nationalityId ? errors?.nationalityId : '')}
                />
                <div className='lg:w-full hidden lg:block'></div>
              </RowView>

              <div className='w-full pt-3 sm:pt-5'>
                <RadioButton
                  label={getContent('are_you_national_of_another_country')}
                  required={false}
                  value={values?.areYouAlsoANationalOfAnotherCountry}
                  onChange={(e) => {
                    setFieldValue('areYouAlsoANationalOfAnotherCountry', e);
                    setFieldTouched('areYouAlsoANationalOfAnotherCountry', false);
                  }}
                  options={optionsYesOrNO}
                />
                <ErrorText text={getContent(touched?.areYouAlsoANationalOfAnotherCountry ? errors?.areYouAlsoANationalOfAnotherCountry : '')} />
              </div>
              {values?.areYouAlsoANationalOfAnotherCountry?.toString() === '1' && (
                <RowView isResponsive>
                  <Dropdown
                    label={getContent('please_specify')}
                    placeholder={getContent('please_select')}
                    options={myArray?.countryListAnotherCountryWithCode?.map((item) => ({
                      id: item?.id,
                      name: item?.name?.displayName,
                      countryCode: item?.name?.countryCode,
                    }))}
                    isMulti
                    selectedValue={values?.anotherNationalityId}
                    valueKey={'name'}
                    isRequired
                    isSearch
                    onSelect={(val) => {
                      setFieldValue('anotherNationalityId', val);
                      setFieldValue('anotherNationalityIdString', val);
                      setFieldTouched('anotherNationalityId', false);
                    }}
                    errorMessage={getContent(touched?.anotherNationalityId ? errors?.anotherNationalityId : '')}
                  />
                  <div className='lg:w-full hidden lg:block'></div>
                  <div className='lg:w-full hidden lg:block'></div>
                </RowView>
              )}

              <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                <CustomInput
                  label={getContent('id_type')}
                  isDisabled={is_editable}
                  value={applicant_type === 'KSA NAtional' ? 'National ID' : applicant_type === 'KSA Resident' ? 'Iqama' : 'Passport'}
                  isRounded
                  isRequired={true}
                />
                <CustomInput
                  label={getContent('passport_no')}
                  isDisabled={is_editable}
                  value={values?.identificationNumber}
                  isRounded
                  isRequired={true}
                  onChange={(e) => {
                    setFieldValue('identificationNumber', e.target.value);
                    setFieldTouched('identificationNumber', true, false);
                  }}
                  placeholder={getContent('id_number')}
                  errorMessage={getContent(touched?.identificationNumber ? errors?.identificationNumber : '')}
                />
                <HijriDatePicker
                  label={getContent('expiry_date')}
                  selectedDate={values?.identificationNumberExpiryDate}
                  onChange={(val) => {
                    setFieldValue('identificationNumberExpiryDate', val ? val : null);
                    setFieldTouched('identificationNumberExpiryDate', false);
                  }}
                  minDate={new Date()}
                  isRequired={true}
                  errorMessage={getContent(touched?.identificationNumberExpiryDate ? errors?.identificationNumberExpiryDate : '')}
                />
              </RowView>

              <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                <CustomDatePicker
                  label={getContent('expiry_date_grg')}
                  selectedDate={values?.identificationNumberExpiryDateGRG ? new Date(values?.identificationNumberExpiryDateGRG) : null}
                  placeholder='YYYY/MM/DD'
                  onChange={(val) => {
                    setFieldValue('identificationNumberExpiryDateGRG', val ? val.toISOString().split('T')[0] : null);
                    setFieldTouched('identificationNumberExpiryDateGRG', false);
                  }}
                  minDate={new Date()}
                  isRequired={true}
                  errorMessage={getContent(touched?.identificationNumberExpiryDateGRG ? errors?.identificationNumberExpiryDateGRG : '')}
                />
                <Dropdown
                  label={getContent('marital_status')}
                  placeholder={getContent('please_select')}
                  options={myArray?.maritalStatus}
                  selectedValue={values?.maritalStatusId}
                  valueKey={'name'}
                  isRequired
                  isSearch
                  onSelect={(val) => {
                    setFieldValue('maritalStatusId', val);
                    setFieldTouched('maritalStatusId', false);
                  }}
                  errorMessage={getContent(touched?.maritalStatusId ? errors?.maritalStatusId : '')}
                />
                <CustomInput
                  label={getContent('no_of_dependents')}
                  value={values?.noOfDependents}
                  maxLength={3}
                  isRounded
                  isRequired={true}
                  onChange={(e) => {
                    setFieldValue('noOfDependents', e.target.value);
                    setFieldTouched('noOfDependents', true, false);
                  }}
                  placeholder={getContent('enter_no_of_dependents')}
                  errorMessage={getContent(touched?.noOfDependents ? errors?.noOfDependents : '')}
                />
              </RowView>

              <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                <div className='w-full'>
                  <Chips
                    label={getContent('educational_level')}
                    value={values?.levelOfEducationId}
                    onChange={(e) => {
                      setFieldValue('levelOfEducationId', e);
                      setFieldTouched('levelOfEducationId', false);
                    }}
                    options={myArray?.levelOfEducation}
                  />
                  <ErrorText
                    text={getContent(touched?.levelOfEducationId ? errors?.levelOfEducationId : '')}
                    className='block'
                  />
                </div>
                <div className='w-full'>
                  <Chips
                    label={getContent('resident_type')}
                    value={values?.residenceTypeId}
                    onChange={(e) => {
                      setFieldValue('residenceTypeId', e);
                      setFieldTouched('residenceTypeId', false);
                    }}
                    options={myArray?.residenceType}
                  />
                  <ErrorText
                    text={getContent(touched?.residenceTypeId ? errors?.residenceTypeId : '')}
                    className='block'
                  />
                </div>
                {/* <div className='lg:w-full hidden lg:block'></div> */}
              </RowView>

              <RowView className='gap-7 pt-3 sm:pt-5' isResponsive>
                <Dropdown
                  placeholder={getContent('please_select')}
                  label={getContent('approximate_net_worth')}
                  options={myArray?.approxNetWorth}
                  selectedValue={values?.approxNetWorthId}
                  valueKey={'name'}
                  isRequired
                  isSearch
                  labelDivClass={'h-11 sm:h-6 md:h-9 xl:h-6'}
                  onSelect={(val) => {
                    setFieldValue('approxNetWorthId', val);
                    setFieldTouched('approxNetWorthId', false);
                  }}
                  errorMessage={
                    getContent(touched?.approxNetWorthId ? errors?.approxNetWorthId : '')
                  }
                />
                <Dropdown
                  label={getContent('approximate_annual_income')}
                  placeholder={getContent('please_select')}
                  options={myArray?.approxAnnualIncome}
                  selectedValue={values?.approxAnnualIncomeId}
                  valueKey={'name'}
                  isRequired
                  isSearch
                  labelDivClass={'h-9 sm:h-6 md:h-9 xl:h-6'}
                  onSelect={(val) => {
                    setFieldValue('approxAnnualIncomeId', val);
                    setFieldTouched('approxAnnualIncomeId', false);
                  }}
                  errorMessage={
                    getContent(touched?.approxAnnualIncomeId ? errors?.approxAnnualIncomeId : '')
                  }
                />
                <div className='lg:w-full hidden lg:block'></div>
              </RowView>

              <div className='w-full h-0.5 bg-border mt-3 sm:mt-5'></div>

              <RowView className='gap-3 sm:gap-7 pt-3 sm:pt-5'>
                <Button
                  text={getContent('save_proceed')}
                  color={'text-white hover:text-primary '}
                  bgColor='bg-primary hover:bg-transparent hover:border-primary'
                  className={'border border-transparent w-44 px-0 text-center'}
                  isDisabled={ind_post_loading}
                  loading={ind_post_loading}
                  onClick={() => {
                    setFieldValue('isSaveExist', false);
                    handleSubmit();
                  }}
                  type={'submit'}
                />
                <Button
                  text={getContent('save_exit')}
                  color={'text-primary hover:text-white'}
                  bgColor='bg-white hover:bg-primary'
                  className={`border border-primary w-44 px-0 text-center`}
                  isDisabled={ind_post_loading}
                  onClick={() => {
                    setFieldValue('isSaveExist', true);
                    handleSubmit();
                  }}
                />
              </RowView>
            </div>
          );
        }}
      </Formik >

      <CustomModal
        isOpen={applicationRejectedModal}
        heading={getContent('application_rejected')}
        color={'text-black'}
        size={fontSizes.h18}
        isFullWidth
        onClose={null}
      >
        <div className='my-5 p-2 border border-border rounded'>
          <Text
            text={rejection_msg}
            isTruncate={false}
            size={fontSizes.h14}
            color='text-black'
            weight={TextWeight.medium}
            className={'block'}
          />
        </div>
        <RowView className='items-center w-full gap-2 mt-3'>
          <Button
            text={getContent('okay_btn')}
            bgColor='bg-primary'
            className={'w-full px-0 text-center capitalize border border-orange'}
            onClick={() => handleRejectionModalClose()}
          />
        </RowView>
      </CustomModal>
    </>
  );
};

export default PersonalInformation;
